// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {StreamerModeGate} from '@app/features/streamer_mode/components/StreamerModeGate';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import {AccountNavigationSection} from '@app/features/user/components/modals/tabs/account_security_tab/AccountNavigationSection';
import {AccountNestedPage} from '@app/features/user/components/modals/tabs/account_security_tab/AccountNestedPage';
import styles from '@app/features/user/components/modals/tabs/account_security_tab/AccountSecurityTabInline.module.css';
import {AccountTabContent as AccountTab} from '@app/features/user/components/modals/tabs/account_security_tab/AccountTab';
import {DangerZoneTabContent as DangerZoneTab} from '@app/features/user/components/modals/tabs/account_security_tab/DangerZoneTab';
import {SecurityTabContent as SecurityTab} from '@app/features/user/components/modals/tabs/account_security_tab/SecurityTab';
import {isAccountNestedSettingsTab} from '@app/features/user/components/settings_utils/SettingsNavigationGroups';
import type {UserSettingsTabType} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import Users from '@app/features/user/state/Users';
import WebAuthnCredentials from '@app/features/user/state/WebAuthnCredentials';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const ACCOUNT_SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Account',
	comment: 'Account & security settings: section heading for general account info (email, password).',
});
const SECURITY_SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Security',
	comment: 'Account & security settings: section heading for security features (2FA, passkeys).',
});
const DANGER_ZONE_SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Danger zone',
	comment:
		'Account & security settings: section heading for destructive actions (log out everywhere, disable, delete account). Keep plain and unambiguous.',
});
interface AccountSecurityInlineTabProps {
	settingsTabType?: UserSettingsTabType;
}

export const AccountSecurityInlineTab = observer(({settingsTabType}: AccountSecurityInlineTabProps) => {
	const {i18n} = useLingui();
	const user = Users.currentUser;
	const [showMaskedEmail, setShowMaskedEmail] = useState(false);
	if (!user) return null;
	if (isAccountNestedSettingsTab(settingsTabType)) {
		return <AccountNestedPage page={settingsTabType} data-flx="user.account-security-tab.inline.account-nested-page" />;
	}
	if (StreamerMode.shouldHidePersonalInformation) {
		return (
			<div className={styles.container} data-flx="user.account-security-tab.inline.streamer-mode-gate.container">
				<StreamerModeGate />
			</div>
		);
	}
	const isClaimed = user.isClaimed();
	const passkeys = isClaimed ? WebAuthnCredentials.credentials : [];
	return (
		<div className={styles.container} data-flx="user.account-security-tab.inline.account-security-inline-tab.container">
			<AccountNavigationSection data-flx="user.account-security-tab.inline.account-navigation-section" />
			{isClaimed && (
				<SettingsSection
					id="account"
					title={i18n._(ACCOUNT_SECTION_TITLE_DESCRIPTOR)}
					data-flx="user.account-security-tab.inline.account-security-inline-tab.account"
				>
					<AccountTab
						user={user}
						isClaimed={isClaimed}
						showMaskedEmail={showMaskedEmail}
						setShowMaskedEmail={setShowMaskedEmail}
						data-flx="user.account-security-tab.inline.account-security-inline-tab.account-tab"
					/>
				</SettingsSection>
			)}
			{isClaimed && (
				<SettingsSection
					id="security"
					title={i18n._(SECURITY_SECTION_TITLE_DESCRIPTOR)}
					data-flx="user.account-security-tab.inline.account-security-inline-tab.security"
				>
					<SecurityTab
						user={user}
						isClaimed={isClaimed}
						passkeys={passkeys}
						data-flx="user.account-security-tab.inline.account-security-inline-tab.security-tab"
					/>
				</SettingsSection>
			)}
			<SettingsSection
				id="danger_zone"
				title={i18n._(DANGER_ZONE_SECTION_TITLE_DESCRIPTOR)}
				data-flx="user.account-security-tab.inline.account-security-inline-tab.danger-zone"
			>
				<DangerZoneTab
					user={user}
					isClaimed={isClaimed}
					data-flx="user.account-security-tab.inline.account-security-inline-tab.danger-zone-tab"
				/>
			</SettingsSection>
		</div>
	);
});
